/**
 * Descriptor-driven Large UPS Summary source (#997 — Story 4.3).
 *
 * Reproduces the bespoke `getLargeUpsEditorSummaryDefinitions()` +
 * `getLargeUpsNoteSummaryDefinitions()` row sets and per-unit value / title
 * projection from the Large UPS descriptor + the shared Summary factory,
 * byte-identically against the Story 4.3 golden masters
 * (`large-ups-summary-rows.json`, `large-ups-summary-filled.json`). This is
 * the engine-driven replacement Story 4.3 re-points the Summary and the
 * golden tests at — same oracle, new implementation.
 *
 * Row-set layout mirrors what the bespoke code produced from
 * `requestFieldRegistry.ts`:
 *
 *   - Per-unit editor group (`subsectionId: 'largeUps'`): `name`, `status`,
 *     `powerExistingSiteProtectedByLargeKwUps`, `powerLargeUpsDescription`,
 *     `powerModelOfExistingUps`, `powerLevelOfRedundancy`,
 *     `powerRequiredRedundancyForUps`, `powerBatteryChemistry`,
 *     `powerBatteryRuntimeRequiredMin` — the `subsection` marker stamps on
 *     the first row (via `buildEditorSubsectionSummaryRows`).
 *   - Per-unit note group (`subsectionId: 'additionalInformation'`):
 *     `powerAdditionalInformation` (labelOverride "Additional Large UPS Info")
 *     — marker stamps here.
 *
 * Large UPS has NO request-level shared field (contrast Liquid Cooling's
 * `lcLiquidToChipDesired`), so `sharedFieldKeys` is empty on the descriptor
 * and there is no shared-row accessor here.
 *
 * The identity fields `name`/`status` are not registry fields, so their labels
 * fall back to the editor-slice `shortLabel` values ("Name" / "Type") supplied
 * by `LARGE_UPS_EDITOR_SUBSECTION_FIELDS`. No `labelOverrides` are needed for
 * them (the fallback is already correct); the ONLY label override is the
 * additional-info note label (`Additional Large UPS Info`), matching bespoke.
 */

import { LARGE_UPS_DESCRIPTOR } from './largeUps.descriptor';
import { repeatableInstanceNameTypeRowValue } from '../../summary/repeatableInstanceSummaryDisplay';
import { buildEditorSubsectionSummaryRows } from '../fields/projections/editorSubsectionSummaryRows';
import { facilityPowerSummaryRowValue } from '../fields/projections/summaryRowValues';
import {
  LARGE_UPS_EDITOR_SUBSECTION_FIELDS,
  LARGE_UPS_NOTES_SUBSECTION_FIELDS,
} from '../fields/requestFieldRegistry';
import { FACILITY_POWER_FIELDS } from '../fields/steps/facility-power.fields';

import type { SummaryCardRow } from '../../summary/summaryCardSourceTypes';
import type { LargeUpsConfiguration } from '../../types/largeUps';
import type { SubsectionFieldDescriptor } from '../../utils/subsectionHeaderMetrics';

const RBAC_ANCHOR = LARGE_UPS_DESCRIPTOR.permissionRef.rbacAnchorDataField;
const NOTE_RBAC_ANCHOR = 'powerAdditionalInformation';

/**
 * Registry field metadata (labels) the factory resolves per field. Keeping this
 * pointed at the FACILITY_POWER_FIELDS registry (rather than the descriptor's
 * `fields`) mirrors the bespoke source and keeps labels/subsection metadata
 * flowing through the same lookup the engine expects.
 */
const REGISTRY_FIELDS = FACILITY_POWER_FIELDS;

/** Bespoke label override for the note field (`largeUpsNoteSummaryRows`). */
const LARGE_UPS_NOTE_LABEL_OVERRIDES: Readonly<Record<string, string>> = {
  powerAdditionalInformation: 'Additional Large UPS Info',
};

function buildGroup(
  editorFields: readonly SubsectionFieldDescriptor[],
  subsectionId: string,
  rbacAnchorDataField: string,
  labelOverrides?: Readonly<Record<string, string>>,
): SummaryCardRow[] {
  return buildEditorSubsectionSummaryRows({
    editorFields,
    registryFields: REGISTRY_FIELDS,
    rbacAnchorDataField,
    subsectionId,
    labelOverrides,
  });
}

/**
 * Full ordered per-unit Large UPS Summary row set (editor + note),
 * byte-identical to the bespoke
 * `getPerLargeUpsSummaryDefinitions()` output (which concatenates
 * `getLargeUpsEditorSummaryDefinitions()` + `getLargeUpsNoteSummaryDefinitions()`).
 */
export function getLargeUpsDescriptorSummaryDefinitions(): SummaryCardRow[] {
  return [
    ...buildGroup(LARGE_UPS_EDITOR_SUBSECTION_FIELDS, 'largeUps', RBAC_ANCHOR),
    ...buildGroup(
      LARGE_UPS_NOTES_SUBSECTION_FIELDS,
      'additionalInformation',
      NOTE_RBAC_ANCHOR,
      LARGE_UPS_NOTE_LABEL_OVERRIDES,
    ),
  ];
}

/**
 * Per-unit Large UPS Summary value — delegates to
 * `repeatableInstanceNameTypeRowValue` for the identity fields (`name`,
 * Existing/New `status`) and `facilityPowerSummaryRowValue` for the LU detail
 * fields (pass-through with defensive typing). Re-homed here so the Summary
 * value path no longer routes through the bespoke display module.
 */
export function largeUpsDescriptorUnitRowValue(
  unit: LargeUpsConfiguration,
  dataField: string,
): string | number | boolean | null | undefined {
  const nameTypeValue = repeatableInstanceNameTypeRowValue(unit, dataField);
  if (nameTypeValue !== undefined) {
    return nameTypeValue;
  }
  return facilityPowerSummaryRowValue(
    unit as unknown as Record<string, unknown>,
    dataField,
  );
}

/**
 * Resolve a Large UPS unit's display name (user-set `name` → `Large UPS N`
 * fallback based on `displayOrder`, else `null` so the caller falls back to
 * the ordinal). Kept here so the Summary title no longer depends on the
 * bespoke display module.
 */
function resolveLargeUpsSummaryDisplayName(
  unit: LargeUpsConfiguration,
): string | null {
  const name = unit.name?.trim();
  if (name) {
    return name;
  }
  if (unit.displayOrder != null && unit.displayOrder > 0) {
    return `Large UPS ${unit.displayOrder}`;
  }
  return null;
}

/** Per-unit Summary card title — `Existing/New — <name>` (fallback `… Large UPS N`). */
export function getLargeUpsDescriptorUnitTitle(
  unit: LargeUpsConfiguration,
  index: number,
): string {
  const status = unit.status === 'existing' ? 'Existing' : 'New';
  const name = resolveLargeUpsSummaryDisplayName(unit);
  return name ? `${status} — ${name}` : `${status} Large UPS ${index + 1}`;
}
